import type { FC } from 'react';

import type { FooterProps, HeaderProps } from '@/features/storefront/layouts/chrome/types';
import type { ProductViewProps } from '@/features/storefront/layouts/product/types';
import type { PartPresentation } from '@/features/storefront/parts/style';
import type { ThemePartDefaults } from '@/features/storefront/sections/partDocument';
import type { ThemePageDefaults } from '@/features/storefront/sections/pageDocument';
import type { PageId } from '@/features/storefront/sections/pages';
import type { AnchorSectionType, PartType } from '@/features/storefront/sections/parts';
import type { SectionType } from '@/features/storefront/sections/schema';
import { BASE_THEME_ID } from '@/previewThemes/meta';
import {
  BASE_BLOG,
  BASE_CART,
  BASE_CATEGORY,
  BASE_CHECKOUT,
  BASE_LISTING,
  BASE_PRODUCT_RAILS,
  BASE_SEARCH,
  type BlogSpec,
  type CartSpec,
  type CategorySpec,
  type CheckoutSpec,
  type ListingSpec,
  type RegisteredTheme,
  type SearchSpec,
  type SectionPreset,
  type ThemeAnchorParts,
  type ThemePackage,
  type ThemePageSpec,
} from '@/previewThemes/types';

/**
 * Every theme's markup, discovered by folder.
 *
 * A theme is added by creating `previewThemes/<id>/meta.ts` and `theme.tsx` -
 * no map in this file, no enum in the storefront and no switch anywhere gets a
 * new arm. The glob is eager because the shell needs the active theme's header
 * on its first paint, and a lazily-loaded header is a shop that renders
 * headless for a frame.
 *
 * Importing this module pulls in every theme's components. Screens that only
 * need a theme's name or palette - the merchant's gallery, the settings select
 * - should read `meta.ts` instead and stay clear of the markup.
 */
const modules = import.meta.glob<{ theme: ThemePackage }>('./*/theme.tsx', { eager: true });

function folderId(path: string): string {
  // './voltic/theme.tsx' -> 'voltic'
  return path.split('/')[1];
}

export const THEMES: Record<string, RegisteredTheme> = Object.fromEntries(
  Object.entries(modules).map(([path, module]) => {
    const id = folderId(path);
    return [id, { ...module.theme, id }];
  }),
);

/**
 * The theme a document names, or the base theme.
 *
 * Falling back rather than throwing is deliberate, for the same reason
 * `themeTokens` falls back: a saved document naming a theme this build does
 * not ship should render the shop, not a blank page.
 */
export function themePackage(id: string): RegisteredTheme {
  return THEMES[id] ?? THEMES[BASE_THEME_ID];
}

/**
 * One component slot, resolved through the base theme.
 *
 * The base theme supplies every component slot, which is what makes the cast
 * safe: a build whose base theme has no header has a broken storefront in
 * every theme, and it fails at the first paint rather than quietly in one.
 */
function component<K extends 'Header' | 'Footer' | 'Product'>(
  id: string,
  slot: K,
): NonNullable<ThemePackage[K]> {
  return (themePackage(id)[slot] ?? THEMES[BASE_THEME_ID][slot]) as NonNullable<ThemePackage[K]>;
}

export function themeHeader(id: string): FC<HeaderProps> {
  return component(id, 'Header');
}

export function themeFooter(id: string): FC<FooterProps> {
  return component(id, 'Footer');
}

export function themeProduct(id: string): FC<ProductViewProps> {
  return component(id, 'Product');
}

/** Whether the shop's own suggestion rails stand under this theme's product view. */
export function themeProductRails(id: string): boolean {
  return themePackage(id).productRails ?? BASE_PRODUCT_RAILS;
}

/** Whether the quick cart plugin is on for this theme until the merchant chooses. */
export function themeQuickCart(id: string): boolean {
  return themePackage(id).quickCart ?? false;
}

/** A spec slot: the base values, with whatever this theme overrides on top. */
function spec<T>(base: T, override: Partial<T> | undefined): T {
  return override ? { ...base, ...override } : base;
}

export function themeListing(id: string): ListingSpec {
  return spec(BASE_LISTING, themePackage(id).listing);
}

export function themeCart(id: string): CartSpec {
  return spec(BASE_CART, themePackage(id).cart);
}

export function themeCheckout(id: string): CheckoutSpec {
  return spec(BASE_CHECKOUT, themePackage(id).checkout);
}

export function themeSearch(id: string): SearchSpec {
  return spec(BASE_SEARCH, themePackage(id).search);
}

export function themeBlog(id: string): BlogSpec {
  return spec(BASE_BLOG, themePackage(id).blog);
}

export function themeCategory(id: string): CategorySpec {
  return spec(BASE_CATEGORY, themePackage(id).category);
}

// --- Pages ----------------------------------------------------------------
/**
 * What a theme says about one page, or nothing.
 *
 * `undefined` rather than an empty object, so a caller can tell "this theme has
 * no opinion" - which means the shop's defaults - from "this theme offers an
 * empty palette", which nobody should ever declare but which would otherwise
 * read the same.
 */
export function themePageSpec(id: string, page: PageId): ThemePageSpec | undefined {
  return themePackage(id).pages?.[page];
}

/** The section types this theme offers on a page, or `undefined` for all of them. */
export function themePageAvailable(
  id: string,
  page: PageId,
): readonly SectionType[] | undefined {
  return themePageSpec(id, page)?.available;
}

/**
 * This theme's per-page defaults, in the shape `pageSections` asks for.
 *
 * One closure over the theme rather than a lookup per page, because every
 * caller already knows which theme it is rendering and none of them should
 * have to thread the id through the document reader.
 */
export function themePageDefaults(id: string): ThemePageDefaults {
  return (page) => themePageSpec(id, page)?.defaults?.();
}

/**
 * The sections this theme offers pre-arranged for one page.
 *
 * Filtered on two things: whether the preset says it belongs on this page, and
 * whether the section it builds is one the theme still offers there. A theme
 * that narrows its product page to a tight column and also ships a slideshow
 * preset has contradicted itself, and the narrower statement wins.
 */
export function themePresets(
  id: string,
  page: PageId,
  available?: readonly SectionType[],
): readonly SectionPreset[] {
  const presets = themePackage(id).presets ?? [];
  return presets.filter(
    (preset) =>
      (!preset.pages || preset.pages.includes(page)) &&
      (!available || available.includes(preset.type)),
  );
}

// --- Page components ------------------------------------------------------
/**
 * What a theme says about the components inside one page, or nothing.
 *
 * `undefined` rather than an empty object for the same reason `themePageSpec`
 * does it: a caller has to be able to tell "this theme has no opinion" - which
 * means the shop's own arrangement - from "this theme offers nothing", which
 * would be a page with no product on it.
 */
export function themeAnchorParts(
  id: string,
  anchor: AnchorSectionType,
): ThemeAnchorParts | undefined {
  return themePackage(id).parts?.[anchor];
}

/**
 * The regions of a page this theme draws, or `undefined` for all of them.
 *
 * `undefined` rather than the full list, so a caller can tell "this theme draws
 * the page the ordinary way" from "this theme draws exactly these" without
 * having to compare two arrays.
 */
export function themePartRegions(
  id: string,
  anchor: AnchorSectionType,
): readonly string[] | undefined {
  return themeAnchorParts(id, anchor)?.regions;
}

/** The component types this theme offers on a page, or `undefined` for all of them. */
export function themePartsAvailable(
  id: string,
  anchor: AnchorSectionType,
): readonly PartType[] | undefined {
  return themeAnchorParts(id, anchor)?.available;
}

/**
 * This theme's per-page component defaults, in the shape `anchorParts` asks for.
 *
 * One closure over the theme rather than a lookup per page, because every caller
 * already knows which theme it is rendering and none of them should have to
 * thread the id through the document reader.
 */
export function themePartDefaults(id: string): ThemePartDefaults {
  return (anchor) => themeAnchorParts(id, anchor)?.defaults?.();
}

/**
 * How this theme draws the components of one page while nobody has restyled
 * them. See `PartPresentation`.
 */
export function themePartPresentation(
  id: string,
  anchor: AnchorSectionType,
): PartPresentation | undefined {
  return themeAnchorParts(id, anchor)?.presentation;
}
